import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ArtifactOptions, CopilotWrapperProps } from "./types";
import { AppContext } from "@/AppContext";
import { useNavigate, useLocation } from "react-router-dom";
import { head } from "lodash";
import { getCampaign, getObjects } from "@/api/campaigns";
import { getSessionToken, isLoggedIn } from "@/api/auth";
import { AI_DOMAIN, LANGGRAPH_DOMAIN } from "@launchos/shared-utils/env";
import { loadChats } from "./CopilotGallery";
import type { Message } from "@langchain/langgraph-sdk";
import { Loading } from "@/ui/Layout/Loading";
import { ArtifactHeader } from "./Artifacts/ArtifactHeader";
import { TopbarButton } from "@/ui/Layout/Topbar/Topbar";
import { HistoryIcon, Plus, X } from "lucide-react";
import { ButtonTypes, DropDownTypes, Variants } from "@/ui/types";
import Dropdown from "@/ui/Dropdown";
import TabsComponent from "@/ui/TabsComponent";
import { Settings } from "../Dashboards/Settings";
import LangGraphBot from "./LangGraphBot";
import { copilotStatus } from "./useClaudeStream";
import { copilotArtifacts } from "./CopilotCards";
import { screenFrom } from "./copilotScreen";
import {
  Browsers,
  CaretRight,
  ThumbsDown,
  ThumbsUp,
  TreeStructure,
} from "@phosphor-icons/react";
import { NewspaperClipping } from "@phosphor-icons/react";
import { cleanThisContent } from "../Pages/V2Editor/actions";
import { addBodyIfNone } from "./Artifacts/PageBuilderArtifact";
import { ISettings } from "../Pages/Editor/types";
import { unmapCampaignVariables } from "../Pages/utils";
import { RenderArtifactContainer } from "./Artifacts/RenderArtifactContainer";
import { CopilotHooks } from "@/plugins/types";
import { triggerHook } from "@/plugins/client";
import { HookTriggers } from "@/plugins/types";
import { mergeContextChips } from "./utils";
import { createOrUpdateChat, getChat } from "@/api/ai";
import { AgentMode, AgentModeButton } from "./AgentModeButton";
import {
  MessageType,
  PromptInputButtonSettings,
} from "@/ui/CommunicationView/ChatConversation/types";
import Button from "@/ui/Button";

import { ASSISTANT_IDS } from "./env";
import { StatusBar } from "./LangGraphBot/StatusBar";
import {
  campaignBuilderArtifacts,
  pageBuilderArtifacts,
} from "./LangGraphBot/components/Artifacts";
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import PureCampaignBuilder from "../Funnels/CampaignBuilder/PureCampaignBuilder";
import useWindowSize from "react-use/lib/useWindowSize";
import Confetti from "react-confetti";
import { ItemGroup } from "../Launchpad/components/ItemGroup";
import { communitySessions } from "./LangGraphBot/data";
import { CardTypes } from "../Launchpad/components/Card";

const feedbackButtons = [
  {
    onClick: async (run_id: string, caption: string) => {
      const response = window.prompt(
        "Please share a brief description of why you liked this response"
      );
      if (response) {
        console.log("response", response, run_id, caption);
        const r = await fetch(`${AI_DOMAIN}/api/langsmith/feedback`, {
          method: "post",
          body: JSON.stringify({ run_id, feedback: response, score: 1 }),
        });
        const data = await r.json();
        console.log("data", data);
        alert("Your feedback has been submitted.  Thanks!");
      }
    },
    icon: (
      <ThumbsUp
        size={24}
        className="p-1 dark:text-white/50 text-black/50 dark:hover:text-white hover:text-black transition-colors duration-200"
      />
    ),
  },
  {
    onClick: async (run_id: string, caption: string) => {
      const response = window.prompt(
        "Please share a brief description of why you didn't like this response"
      );
      if (response) {
        console.log("response", response, run_id, caption);
        const r = await fetch(`${AI_DOMAIN}/api/langsmith/feedback`, {
          method: "post",
          body: JSON.stringify({ run_id, feedback: response, score: -1 }),
        });
        const data = await r.json();
        console.log("data", data);
        alert("Your feedback has been submitted.  Thanks!");
      }
    },
    icon: (
      <ThumbsDown
        size={24}
        className="p-1 dark:text-white/50 text-black/50 dark:hover:text-white hover:text-black transition-colors duration-200"
      />
    ),
  },
];

// Starter suggestions for the Claude side (it doesn't use the suggestions server).
const CLAUDE_SUGGESTIONS = [
  { label: "What are my open to-dos?" },
  { label: "What did we decide in the last scrum?" },
  { label: "Which pages on my sites are still drafts?" },
];

export const CopilotWrapperContext = createContext<{
  artifactOptions: ArtifactOptions;
  setArtifactOptions: (options: ArtifactOptions) => void;
}>({
  artifactOptions: {},
  setArtifactOptions: () => {},
});

export const useCopilotWrapper = () => {
  return useContext(CopilotWrapperContext);
};

export const CopilotWrapper: React.FC<CopilotWrapperProps> = ({
  id,
  sessionReplay = false,
  setCampaignName = () => {},
  setCampaignId = () => {},
  setTopBarButtons = () => [],
  children = null,
}) => {
  const [chatList, setChatList] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState("messages");
  const [campaignData, setCampaignData] = useState({});
  const [artifactOptions, setArtifactOptions] = useState<ArtifactOptions>({
    isShowing: Boolean(children),
  });
  const [pageSpec, setPageSpec] = useState<any>({
    variables: unmapCampaignVariables({
      colors: {
        "inverseSurface": "#141414",
        "surfaceContainerHigh": "#202A37",
        "surfaceContainer": "#4B5562",
        "surfaceContainerLow": "#F9FAFB",
        "surface": "#FFFFFF",
        "primary": "#141414",
        "secondary": "#4B5562"
      },
      palette: "monochrome-black-gray",
      companyName: "Company Name Test",
    }),
  });
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingChatList, setIsLoadingChatList] = useState([]);
  const [loggedIn, setIsLoggedIn] = useState(false);
  const [messages, setMessages] = useState([]);
  const [assistantId, setAssistantId] = useState(ASSISTANT_IDS.PAGE); // Page Agent
  const [mode, setMode] = useState<AgentMode>("Page");
  const [statusBarSettings, setStatusBarSettings] = useState<any>(null);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  // CoPilot engine (CP-3): Claude answers when the CoPilot's Claude side is on for this person
  // (Ambid only, COPILOT_CLAUDE switch); otherwise the LangGraph page-builder CoPilot, as before.
  const [engine, setEngine] = useState<"langgraph" | "claude">("langgraph");
  // a message sent to the CoPilot from elsewhere (e.g. a meeting note's "Draft it") waits until we
  // know which engine answers, so it can't go to the old page builder by mistake
  const [engineReady, setEngineReady] = useState(false);
  useEffect(() => {
    let live = true;
    copilotStatus().then((on) => {
      if (!live) return;
      if (on) setEngine("claude");
      setEngineReady(true);
    });
    return () => {
      live = false;
    };
  }, []);
  const [pinnedMessagesBottom, setPinnedMessagesBottom] = useState<MessageType[]>([]);
  // const [initialThread, setInitialThread] = useState<any>(null);
  const [confettiIsShowing, setConfettiIsShowing] = useState(false);
  const [chatKey, setChatKey] = useState(0);

  const currentChatBotRef = useRef<any>(null);
  const campaignBuilderRef = useRef<{ fitView: () => void }>(null);
  const isProcessingQueryRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const contextObj = useContext(AppContext);

  const { copilotContext, currentlyViewing, copilotSettings, setCopilotSettings, setCopilotContext  } = contextObj;

  const loadChat = async (id: string) => {
    setIsLoading(true);
    // const token = getSessionToken();
    // const data = await getCampaign({ token, id });
    // setCampaignData(data);
    // setCampaignName(data?.name);
    // setCampaignId(data?.id);
    // // setIsLoading(false);
    // const response = await getObjects(data?.id);
    // const object = head(response?.data?.objects);
    // const { settings = {} } = object;
    // const {
    //   messages = [],
    //   threadId = null,
    //   /*graphState = {},*/ thread = null,
    // } = settings;

    const data = await getChat(id);
    const { messages = [], threadId, thread, name, type, settings } = data;
    console.log({ messages, threadId, thread, name, type, settings });
    setCampaignData(data);
    setCampaignName(name);
    // setCampaignId(data?.id);

    // const parsedThread = thread ? JSON.parse(thread) : null;
    // const parsedThread = thread;
    // console.log("parsedThread", parsedThread);
    // console.log("parsedThread.history", parsedThread?.history);
    // console.log("parsedThread.values", parsedThread?.values);
    // console.log("parsedThread.checkpoint", parsedThread?.checkpoint);

    setArtifactOptions({
      ...artifactOptions,
      // graphState,
    });

    if (threadId) {
      setThreadId(threadId);
    // if (parsedThread) {
    //   setThreadId(threadId);
    //   setInitialThread(parsedThread);
    } else {
      setMessages(messages);
      // setThreadId(null);
    }

    setIsLoading(false);
  };

  const loadSuggestions = async () => {
    setTopBarButtons(null);
    if (engine === "claude") {
      setSuggestions(CLAUDE_SUGGESTIONS as any);
      return;
    }

    const prompt = "Generate 3 prompt suggestions for a web page building agent that can generate landing pages from scratch, or build on existing pages!";
    const response = await fetch(`${AI_DOMAIN}/api/copilot/suggestions`, {
      method: "post",
      body: JSON.stringify({ prompt }),
    });
    const r = response.json();
    const data = await r;
    console.log("Suggestions", data);
    setSuggestions(data.suggestions);
  };

  const handleOpenHistoryDropdown = async () => {
    if (!Boolean(chatList.length)) {
      setIsLoadingChatList(true);
      loadChats((seqs) => {
        setChatList(seqs);
        setIsLoadingChatList(false);
      });
    }
  };

  const chatSuggestions = suggestions.map((itm: any) => {
    let icon;
    if (itm?.icon === "Browsers") icon = Browsers;
    if (itm?.icon === "TreeStructure") icon = TreeStructure;
    if (itm?.icon === "NewspaperClipping") icon = NewspaperClipping;
    return { ...itm, icon };
  });

  const handleBeforeSubmit = (messages: Message[]) => {
    const { copilotContext, currentlyViewing } = contextObj;

    console.log("Currently viewing", currentlyViewing);
    console.log("Copilot context", copilotContext);

    const mergeObjects = (objects: any[]) => {
      return objects.reduce(
        (acc, obj) => ({
          ...acc,
          ...obj,
        }),
        {}
      );
    };

    const newSystemPromptFromPlugins: string[] = triggerHook(
      HookTriggers.onChatPrompt,
      {
        id: CopilotHooks.GET_SYSTEM_PROMPT,
      },
      { context: currentlyViewing },
      contextObj,
      {}
    );

    const newGraphStateFromPlugins = triggerHook(
      HookTriggers.onChatPrompt,
      {
        id: CopilotHooks.GET_GRAPH_STATE,
      },
      { context: currentlyViewing },
      contextObj,
      {}
    );

    // merge context chips
    const context = mergeContextChips(copilotContext, currentlyViewing);

    // get updated context
    // const updatedGraphState = artifactOptions?.graphState || {};
    // console.log("Updated graph state", updatedGraphState);
    // console.log("Sending this as context", context);

    // get the theme
    console.log("Campaign data", campaignData);
    // const themeId = campaignData?.settings?.themeId || undefined;
    const themeId = "NewWireframesWithLiquid";

    console.log("Selected theme", themeId);

    console.log("Artifact options", artifactOptions);
    const newGraphStateFromArtifactOptions = {
      ...(artifactOptions.container?.pageContent ? { pageContent: artifactOptions.container?.pageContent } : {}),
      ...(artifactOptions.container?.campaignContent ? { campaignContent: artifactOptions.container?.campaignContent } : {}),
      ...(artifactOptions.container?.content ? { content: artifactOptions.container?.content } : {}),
    }

    const finalGraphState = {
      systemPrompt: newSystemPromptFromPlugins.join("\n\n"),
      context,
      ...mergeObjects(newGraphStateFromPlugins),
      // ...updatedGraphState,
      ...newGraphStateFromArtifactOptions,
      pageThemeId: themeId,
    };

    console.log("Final graph state", finalGraphState);

    delete finalGraphState.messages;

    // return false;
    return finalGraphState;
  };

  const handleSetArtifactOptions = (options: any) => {
    console.log("setArtifactOptions called", options);
    
    // Handle pageSpec updates from PageSpec component
    if (options.pageSpec) {
      console.log("Updating pageSpec from artifact:", options.pageSpec);
      setPageSpec(options.pageSpec);
    }
    
    setArtifactOptions(options);
  };

  const handleOnUpdate = async (nodeObject: any) => {
    const nodes = Object.keys(nodeObject); // the names of the nodes being processed
    console.log("nodeObject", nodeObject);
    const graphState = nodeObject[nodes[0]];

    const { pageContent, campaignContent, content } = graphState as {
      pageContent: ISettings[];
      campaignContent: any[];
      messages: Message[];
      content: string;
    };

    console.log("handleOnUpdate", "pageContent", pageContent);
    console.log("handleOnUpdate", "campaignContent", campaignContent);
    console.log("handleOnUpdate", "content", content);

    // Campaign Builder
    if (campaignContent && campaignContent.length > 0) {
      setArtifactOptions({
        ...artifactOptions,
        isShowing: true,
        // graphState,
        ref: campaignBuilderRef,
        container: {
          title: "Funnel Builder",
          type: "campaign_builder",
          pageContent: [],
          campaignContent,
          setOptions: setArtifactOptions,
        },
      });

      // setTimeout(() => {
      campaignBuilderRef.current?.fitView();
      // }, 700);
    }

    // Page Builder
    else if (pageContent && pageContent.length > 0) {
      // console.log("pageContent", pageContent)
      setArtifactOptions({
        ...artifactOptions,
        isShowing: true,
        // graphState,
        container: {
          title: "Page Builder",
          type: "page_builder",
          pageContent: cleanThisContent(
            addBodyIfNone(pageContent)
          ) as ISettings[],
          setOptions: setArtifactOptions,
        },
      });
    }

    // Content Writer
    else if (content && content.length > 0) {
      setArtifactOptions({
        ...artifactOptions,
        isShowing: true,
        // graphState,
        container: {
          title: "Content Writer",
          type: "content_writer",
          content: [{ content }],
          setOptions: setArtifactOptions,
        },
      });
    }

    // save the state in the campaign database
    const { currentThread } = currentChatBotRef?.current || {};
    const { values = {}, history = [] } = currentThread || {};
    const { messages = [] } = values;
    console.log("handleOnUpdate - currentThread", currentThread);
    console.log("handleOnUpdate - history", history);
    console.log("handleOnUpdate - values", values);
    console.log("handleOnUpdate - threadId", threadId);

    if (!sessionReplay && threadId && id) {
      console.log("handleOnUpdate - creating or updating chat", threadId, id);
      const campaign = await createOrUpdateChat({
        messages,
        // graphState: valuesWithoutMessages,
        threadId: threadId || undefined,
        sessionId: id,
        // thread: currentThread, // Use currentThread from ref instead of thread param
        noAppend: true,
      });
      if (!id && campaign) {
        setCampaignId(campaign?.id);
        setCampaignName(campaign?.name);
      }
    }
  };

  const handleOnFinish = async (thread: any) => {
    // save the state in the campaign
    // const { checkpoint, values } = thread;
    // console.log("handleOnFinish - thread param", thread);

    // Get the current thread from the ref which has the history
    const { currentThread, values } = currentChatBotRef?.current || {};
    console.log("handleOnFinish - currentThread from ref", currentThread);
    console.log("handleOnFinish - currentThread.history", currentThread?.history);
    console.log("handleOnFinish - provided thread", thread);

    const { messages = [], pageContent = [], campaignContent = [] } = values || thread?.values;
    console.log("Messages", messages);
    // console.log("Page content", pageContent);
    // console.log("Campaign content", campaignContent);

    const { messages: _omitMessages, ...valuesWithoutMessages } = values || thread?.values;
    // setPinnedMessagesBottom([]);

    // Save the state in the database
    if (!sessionReplay) {
      const campaign = await createOrUpdateChat({
        messages,
        // graphState: valuesWithoutMessages,
        threadId: threadId || thread?.metadata?.thread_id || undefined,
        sessionId: id,
        thread: currentThread, // Use currentThread from ref instead of thread param
        noAppend: true,
      });
      if (campaign) {
        setCampaignId(campaign?.id);
        setCampaignName(campaign?.name);
      }
    }

    // Page Builder
    if (pageContent && pageContent.length > 0) {
      const options = {
        ...artifactOptions,
        isShowing: true,
        container: {
          title: "Page Builder",
          type: "page_builder",
          pageContent,
        },
      };

      setStatusBarSettings({
        ...statusBarSettings,
        isShowing: true,
        icon: Browsers,
        caption: "Page Builder",
        actionPayload: {
          label: "Open Page Builder",
          onClick: () => {
            setArtifactOptions(options);
          },
        },
        className: "bg-yellow-500",
      });

      setArtifactOptions(options);
    }

    // Campaign Builder 
    if (campaignContent && campaignContent.length > 0) {
      setStatusBarSettings({
        ...statusBarSettings,
        // isExpanded: true,
        icon: TreeStructure,
        caption: "Campaign Builder",
        className: "bg-yellow-500",
        childComponent: (props: any) => (
          <div className="h-[275px] p-4 w-full">
            <PureCampaignBuilder
              showControls={false}
              showMiniMap={false}
              readOnly={true}
              {...props}
            />
          </div>
        ),
        childComponentProps: {
          objects: campaignContent,
          ref: campaignBuilderRef,
        },
      });

      setArtifactOptions({
        ...artifactOptions,
        // isShowing: sessionReplay ? true : artifactOptions.isShowing,
        isShowing: true,
        ref: campaignBuilderRef,
        container: {
          ...artifactOptions.container,
          title: "Funnel Builder",
          type: "campaign_builder",
          // pageContent: [],
          campaignContent,
        },
      });
      
      // Fit the campaign view to show all nodes
      campaignBuilderRef.current?.fitView();
    }

    // Show confetti if the campaign or page content has changed
    if (sessionReplay && (campaignContent && campaignContent.length > 0 || (pageContent && pageContent.length > 0))) {
      setConfettiIsShowing(true);
    }

  };

  const handleInterrupt = (value: any, resumable: boolean) => {
    console.log("onInterrupt", value, resumable);
    if (Object.keys(value).includes("pageSpec")) {
      setPinnedMessagesBottom([
        {
          id: "page-spec",
          caption: JSON.stringify({ ...value.pageSpec, showFooter: true }),
          type: "ai",
          from: "Bot",
          name: "Page Spec",
        },
      ]);
    }
  };

  const handleOnLoad = async (state: any) => {
    const {
      pageContent,
      campaignContent,
      content,
      status = { caption: "Finished Build" },
    } = state || {};
    
    const newStatusBarSettings = {
      ...statusBarSettings,
      caption: status.caption,
      showCloseButton: false,
    };
    
    console.log("handleOnLoad - state", state, newStatusBarSettings);

    if (pageContent && pageContent.length > 0) {

      const options = {
        ...artifactOptions,
        isShowing: true,
        // container: children ? undefined : {
        container: {
          title: "Page Builder",
          type: "page_builder",
          pageContent,
        },
      };

      setStatusBarSettings({
        ...newStatusBarSettings,
        isShowing: true,
        icon: Browsers,
        caption: "Page Builder",
        actionPayload: {
          label: "Open Page Builder",
          onClick: () => {
            setArtifactOptions(options);
          },
        },
        className: "bg-yellow-500",
      });

      setArtifactOptions({ isShowing: Boolean(children), container: undefined });

    }

    if (campaignContent && campaignContent.length > 0) {
      setStatusBarSettings({
        ...newStatusBarSettings,
        isShowing: true,
        actionPayload: {
          label: "Open Funnel Builder",
          onClick: () => {
            setArtifactOptions({
              isShowing: true,
              container: {
                title: "Funnel Builder",
                type: "campaign_builder",
                // pageContent: [],
                campaignContent,
              },
            });
          },
        },
      });
    }

    if (content && content.length > 0) {
      setStatusBarSettings({
        ...newStatusBarSettings,
        isShowing: true,
        actionPayload: {
          label: "Open Content Writer",
          onClick: () => {
            setArtifactOptions({
              isShowing: true,
              container: {
                title: "Content Writer",
                type: "content_writer",
                content: [{ content }],
                setOptions: setArtifactOptions,
              },
            });
          },
        },
      });
    }

    console.log("Current chat bot ref values", state);
  };

  const handleChatReset = () => {
    setCampaignData({});
    setCampaignId("");
    setCampaignName("");
    setThreadId(null);
    setArtifactOptions({ isShowing: children ? true : false, container: undefined });
    setActiveTab("messages");
    setStatusBarSettings({ isShowing: false });
    setMessages([]);
    setPinnedMessagesBottom([]);
    setConfettiIsShowing(false);
    setIsLoading(false);
    setIsLoadingChatList(false);
    setChatKey(chatKey + 1);
    loadSuggestions();
    // reset the chat bot
    currentChatBotRef.current.threadId = null;
  }

  const { width, height } = useWindowSize();

  useEffect(() => {
    (async () => {
      if (!id) setIsLoading(false);
      setActiveTab("messages");
      const isTheUserLoggedIn = await isLoggedIn();
      setIsLoggedIn(isTheUserLoggedIn);

      if (isTheUserLoggedIn) {
        // console.log("Copilot Wrapper", "isTheUserLoggedIn", isTheUserLoggedIn);
        if (id) { loadChat(id);
        } else if (children) {
          setArtifactOptions({ isShowing: true });
        } else {
          setArtifactOptions({ isShowing: false });

          loadSuggestions();
          setIsLoadingChatList(true);
          loadChats((seqs) => {
            setChatList(seqs);
            setIsLoading(false);
            setIsLoadingChatList(false);
          });
        }
      } else {
        loadSuggestions();
      }
    })();
  }, [id]);

  useEffect(() => {
    if (engineReady && copilotContext?.query && !isProcessingQueryRef.current) {
      console.log("Copilot Wrapper", "Processing copilot context query", copilotContext.query);

      // Immediately clear the query to prevent multiple processing
      const queryToProcess = copilotContext.query;
      setCopilotContext({ ...copilotContext, query: undefined });

      // Wait for currentChatBotRef.current to be available, up to 10 seconds
      const waitForChatBotRef = async (timeout = 10000, interval = 100) => {
        const start = Date.now();
        return new Promise((resolve, reject) => {
          const check = () => {
            if (currentChatBotRef.current) {
              resolve(currentChatBotRef.current);
            } else if (Date.now() - start >= timeout) {
              resolve(null);
            } else {
              setTimeout(check, interval);
            }
          };
          check();
        });
      };

      (async () => {
        const ref = await waitForChatBotRef();
        if (ref) {
          // Check if the thread is already running a task
          const isThreadBusy = ref.currentThread?.values?.isRunning || 
                              ref.currentThread?.values?.status === 'running' ||
                              ref.isProcessing;
          
          if (isThreadBusy) {
            console.log("Thread is busy, waiting before submitting new query...");
            // Wait a bit and try again
            setTimeout(() => {
              if (ref && !ref.isProcessing && !isProcessingQueryRef.current) {
                isProcessingQueryRef.current = true;
                ref.submit({
                  ...handleBeforeSubmit(ref.currentThread?.values?.messages || []),
                  pageThemeId: "NewWireframesWithLiquid",
                  messages: [{ type: "human", content: queryToProcess }],
                });
                isProcessingQueryRef.current = false;
              }
            }, 2000);
            return;
          }

          // Add a small delay to ensure thread is ready
          await new Promise(resolve => setTimeout(resolve, 100));
          
          isProcessingQueryRef.current = true;
          ref.submit({
            ...handleBeforeSubmit(ref.currentThread?.values?.messages || []),
            pageThemeId: "NewWireframesWithLiquid",
            messages: [{ type: "human", content: queryToProcess }],
          });
          isProcessingQueryRef.current = false;
        }
      })();
    }

    console.log("Copilot Wrapper", "Copilot context", copilotContext);
    console.log("Copilot Wrapper", "Currently viewing", currentlyViewing);
    const keys = Object.keys(copilotContext);

    if (keys.includes("Pages")) {
      if (currentlyViewing.find((itm) => itm.label === "Pages")) {
        setMode("Page");
        setAssistantId(ASSISTANT_IDS.PAGE);
      }
    }
    if (keys.includes("Funnel Objects") || keys.includes("Funnels")) {
      if (
        currentlyViewing.find((itm) => itm.label === "Funnels") ||
        currentlyViewing.find((itm) => itm.label === "Funnel Objects")
      ) {
        setMode("Funnel");
        setAssistantId(ASSISTANT_IDS.CAMPAIGN);
      }
    }
    if (keys.includes("Content Library")) {
      if (currentlyViewing.find((itm) => itm.label === "Content Library")) {
        setMode("Copy");
        setAssistantId(ASSISTANT_IDS.COPY);
      }
    }
    if (keys.includes("Planning Profiles")) {
      if (currentlyViewing.find((itm) => itm.label === "Planning")) {
        setMode("Research");
        setAssistantId(ASSISTANT_IDS.RESEARCH);
      }
    }
  }, [copilotContext, currentlyViewing, engineReady]);

  // Close artifact container when user navigates away from the page
  useEffect(() => {
    if (artifactOptions?.container) {
      setArtifactOptions({
        ...artifactOptions,
        container: undefined,
      });
    }
    loadSuggestions();
  }, [location.pathname]);

  const agentModeButton: PromptInputButtonSettings = {
    position: "inside",
    location: "beforeInput",
    icon: (
      <AgentModeButton
        type={mode}
        onModeChange={(mode: AgentMode) => {
          setMode(mode);
          if (mode === "Page") setAssistantId(ASSISTANT_IDS.PAGE); // Page Agent
          if (mode === "Funnel") setAssistantId(ASSISTANT_IDS.CAMPAIGN); // Funnel Agent
          if (mode === "Copy") setAssistantId(ASSISTANT_IDS.COPY); // Copy Agent
          if (mode === "Research") setAssistantId(ASSISTANT_IDS.RESEARCH); // Research Agent
        }}
      />
    ),
    onClick: () => console.log("Microphone button clicked"),
  };

  return (
    <CopilotWrapperContext.Provider
      value={{ artifactOptions, setArtifactOptions }}
    >
      {confettiIsShowing && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          onConfettiComplete={() => setConfettiIsShowing(false)}
        />
      )}

      {/* <DndProvider backend={HTML5Backend}> */}
      {/* <div className="flex flex-col"> */}
      <div className="h-full w-full flex flex-col">
        <div
          className={`gap-6 ${id || threadId ? "h-full" : "h-auto"} w-full ${artifactOptions?.isShowing && activeTab === "messages" ? "flex overflow-hidden" : "max-w-6xl"} mx-auto flex flex-1 items-center justify-center`}
        >
          {isLoading && (
            <div className="w-full h-full px-9 py-96 mx-auto flex items-center justify-center">
              <Loading type="gallery" />
            </div>
          )}

          {!isLoading && (
            <div
              className={`h-[calc(100vh-73px)] p-6 ${artifactOptions?.isShowing && activeTab === "messages" ? "w-1/3" : "max-w-6xl w-full"} justify-between flex flex-col`}
            >
              {/* {JSON.stringify(artifactOptions)} */}
              <div className="tabs flex justify-between">
                {id ? (
                  <TabsComponent
                    tabs={[
                      { label: "Messages", value: "messages" },
                      { label: "Chat Settings", value: "settings" },
                    ]}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    actionSection={false}
                    storeActiveTab={false}
                  />
                ) : (
                  <div />
                )}
                {loggedIn && (
                  <div className="flex gap-2 h-[38px]">
                    <Dropdown
                      type={DropDownTypes.BASIC}
                      label={<HistoryIcon className="w-5 h-5" />}
                      onOpen={handleOpenHistoryDropdown}
                      isLoading={isLoadingChatList}
                      items={chatList.map(({ id, label, caption }) => ({
                        id,
                        label,
                        caption: caption.replace("Last Updated: ", ""),
                      }))}
                      onClick={(key) => {
                        if (campaignData?.id === chatList[key]?.id) return;
                        if (children) {
                          console.log(
                            "handleOnUpdate - chatList[key]",
                            chatList[key]
                          );
                          handleChatReset();
                          setIsLoading(true);
                          setCopilotSettings({
                            ...copilotSettings,
                            id: chatList[key]?.id,
                          });
                          setCampaignId(chatList[key]?.id);
                        } else {
                          setIsLoading(true);
                          navigate(`/copilot/${chatList[key]?.id}`);
                        }
                      }}
                    />

                    <Button
                      // type={ButtonTypes.WITH_ICON}
                      variant={Variants.INFO}
                      onClick={() => {
                        if (children) handleChatReset();
                        else {
                          window.location.href = "/copilot";
                        }
                      }}
                      // defaultIcon={<Plus className="w-5 h-4 max-h-4" />}
                      label="New chat"
                    />

                    {/* <TopbarButton
                      onClick={() => {
                        if (children) handleChatReset();
                        else {
                          window.location.href = "/copilot";
                        }
                      }}
                    >
                      <Plus className="w-5 h-4 max-h-4" />
                      <span>New chat</span>
                    </TopbarButton> */}
                  </div>
                )}
              </div>

              {activeTab === "messages" && (
                <>
                  {/* {JSON.stringify(threadId)} */}
                  <LangGraphBot
                    key={`${chatKey}-${engine}`}
                    ref={currentChatBotRef}
                    engine={engine}
                    getScreen={() => screenFrom(contextObj.currentlyViewing)}
                    assistantId={assistantId}
                    defaultThreadId={threadId || undefined}
                    setDefaultThreadId={setThreadId}
                    apiUrl={LANGGRAPH_DOMAIN}
                    title={engine === "claude" ? "What can I help you with?" : "What can I help you launch today?"}
                    // subtitle="Generate landing pages, sales funnels, copy, and much more!"
                    subtitle={engine === "claude" ? "Ask about your accounts, or tell me what to do. I'll ask before anything goes live." : "Generate landing pages from scratch, or build on existing pages!"}
                    statusBarComponent={StatusBar}
                    // statusBarSettings={statusBarSettings}
                    statusBarSettings={!(artifactOptions?.isShowing && artifactOptions?.container) ? statusBarSettings : undefined}
                    suggestions={engine === "claude" ? CLAUDE_SUGGESTIONS : chatSuggestions}
                    sessionReplay={sessionReplay}
                    sessionReplaySettings={{ speed: 1000, countdown: 4 }}
                    pinnedMessagesBottom={pinnedMessagesBottom}
                    setPinnedMessagesBottom={setPinnedMessagesBottom}
                    withFeedbackButtons
                    feedbackButtons={feedbackButtons}
                    placeholder={engine === "claude" ? "Ask or tell the CoPilot to do something…" : `Ask Launch OS a question...`}
                    artifacts={[
                      ...copilotArtifacts,
                      ...campaignBuilderArtifacts,
                      ...pageBuilderArtifacts,
                    ]}
                    autoScroll
                    initialMessages={messages}
                    // initialThread={initialThread}
                    buttonSettings={engine === "claude" ? [] : [agentModeButton]}
                    artifactOptions={artifactOptions}
                    setArtifactOptions={handleSetArtifactOptions}
                    onBeforeSubmit={handleBeforeSubmit}
                    onInterrupt={handleInterrupt}
                    onUpdate={handleOnUpdate}
                    onFinish={handleOnFinish}
                    onLoad={handleOnLoad}
                    // showingRawContent
                  />
                  {/* {!id &&
                    !threadId &&
                    !sessionReplay &&
                    !artifactOptions?.isShowing && (
                      <ItemGroup
                        title="Featured Campaigns"
                        cardType={CardTypes.CHAT_SESSION}
                        caption="These are some of the most popular campaigns that have been created with Launch OS."
                        items={communitySessions}
                        onItemClick={(item) => {
                          navigate(`/copilot/${item.id}/replay`);
                        }}
                      />
                    )} */}
                </>
              )}

              {activeTab === "settings" &&
                campaignData &&
                Object.keys(campaignData).length > 0 && (
                  <Settings
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    unitName="Chat"
                    widgets={["name", "planner", "theme", "delete"]}
                  />
                )}
            </div>
          )}

          {/* {JSON.stringify(artifactOptions)} */}

          {activeTab === "messages" &&
            (id || children || artifactOptions?.isShowing) && (
              <div
                className={`flex-1 transition-all duration-300 border-l border-black/10 dark:border-white/10 dark:bg-[rgba(22,22,22,0.8)] overflow-y-hidden m-4 border rounded-lg ${
                  artifactOptions?.isShowing
                    ? `translate-x-0 opacity-100 visible ${!children && "h-full"}`
                    : "translate-x-full opacity-0 invisible h-0 absolute"
                } flex flex-col justify-center`}
              >
                {artifactOptions?.container && artifactOptions?.isShowing && (
                  <div className="h-full w-full z-50 opacity-100 flex items-center justify-center absolute">
                    <div className="w-full h-full flex items-center justify-center">
                      <RenderArtifactContainer
                        type={artifactOptions?.container?.type}
                        // content={artifactOptions.container.content || artifactOptions.container.pageContent || artifactOptions.container.campaignContent || []}
                        options={artifactOptions}
                        setOptions={setArtifactOptions}
                        hasChildren={Boolean(children)}
                        mode={mode}
                        thread={currentChatBotRef.current}
                        pageSpec={pageSpec}
                      />
                    </div>
                  </div>
                )}

                {children}
              </div>
            )}
        </div>
      </div>
      {/* </DndProvider> */}
    </CopilotWrapperContext.Provider>
  );
};
